<template>
  <div
    class="timeblock"
    :class="{ 'timeblock--finished': modelValue.is_finished }"
  >
    <a-employee-timeblock-header
      :model-value="modelValue"
      :min-date="minDate"
      :deletable="deletable"
      :is-editable="isEditable"
      :is-time-range-valid="isTimeRangeValid"
      :collision="collision"
      :has-validation-errors="v$.$error && v$.$anyDirty"
      :has-start-time-error="hasStartTimeError"
      :has-end-time-error="hasEndTimeError"
      :end-time-error-message="endTimeErrorMessage"
      :planned-time="_totalPlannedTime"
      :free-time="_freeTime"
      :employee="employee"
      :index="index"
      @previous-day="previousDay"
      @next-day="nextDay"
      @update-date="updateDate"
      @update-start-time="updateStartTime"
      @update-end-time="updateEndTime"
      @add-timeblock="$emit('addTimeblock')"
      @remove-timeblock="$emit('removeTimeblock')"
    />

    <!-- Timeline Visualization -->
    <div v-if="modelValue.date && employee" class="timeline-wrapper">
      <a-employee-timeblock-timeline
        ref="timelineRef"
        :is-loading="isLoading"
        :employee="employee"
        :date="modelValue.date"
        :scheduled-blocks="scheduledBlocks"
        :other-timeblocks="otherTimeblocksForSameDate"
        :current-timeblock-range="currentTimeblockRange"
        :collision="collision"
        :collision-segments="collisionSegments"
        :timeblock-duration="timeblockDuration"
        :start-hour="START_HOUR"
        :end-hour="END_HOUR"
        :hour-width="hourWidth"
        @timeblock-updated="handleTimeblockUpdated"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { useEmployeeTimeline } from '@/plugins/tm@_utils/useEmployeeTimeline';
import {
  timeblockToDateRange,
  detectCollision,
  getCollisionSegments as getCollisionSegmentsUtil,
} from '@/plugins/tm@_utils/timeblock.utils';
import AEmployeeTimeblockHeader from './a-employee-timeblock-header.vue';
import AEmployeeTimeblockTimeline from './a-employee-timeblock-timeline.vue';
import moment from 'moment';
import type { TimeblockData, EmployeeFullData } from './types';

// Configuration: Set to false to allow editing timeblocks with existing IDs
const ALLOW_EDITING_EXISTING_TIMEBLOCKS = true;

interface Props {
  modelValue: TimeblockData;
  employee: EmployeeFullData | null;
  allEmployeeTimeblocks?: TimeblockData[]; // Timeblocks for current employee
  allTimeblocks?: TimeblockData[]; // All timeblocks from all employees
  deletedTimeblockIds?: number[]; // IDs of saved timeblocks that have been deleted
  editingBlockerId?: number | null; // ID of blocker being edited (to exclude from timeline)
  currentIndex?: number;
  deletable?: boolean;
  index?: number;
  draftTimeblocks?: TimeblockData[]; // Draft timeblocks from all employees in draft subtask
}

const props = withDefaults(defineProps<Props>(), {
  allEmployeeTimeblocks: () => [],
  allTimeblocks: () => [],
  deletedTimeblockIds: () => [],
  editingBlockerId: null,
  currentIndex: 0,
  deletable: false,
  index: 0,
  draftTimeblocks: () => [],
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: TimeblockData): void;
  (e: 'addTimeblock'): void;
  (e: 'removeTimeblock'): void;
}>();

const timelineRef = ref<InstanceType<typeof AEmployeeTimeblockTimeline> | null>(
  null,
);

// Check if timeblock is editable (based on whether it has an ID)
const isEditable = computed(() => {
  if (ALLOW_EDITING_EXISTING_TIMEBLOCKS) return true;
  return !props.modelValue.id;
});

// Minimum date is today (no past dates)
const minDate = ref(new Date());

// Timeline configuration
const HOUR_WIDTH = 60; // pixels per hour (desktop)
const HOUR_WIDTH_MOBILE = 50; // pixels per hour (mobile)
const START_HOUR = 0; // Start from midnight
const END_HOUR = 24; // End at midnight

// Check if mobile
const isMobile = ref(false);
onMounted(() => {
  isMobile.value = window.innerWidth <= 768;
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth <= 768;
  });

  // Center timeline on mount
  nextTick(() => {
    centerTimeline();
  });
});

const hourWidth = computed(() =>
  isMobile.value ? HOUR_WIDTH_MOBILE : HOUR_WIDTH,
);

// Custom validator for time range
const timeRangeValid = (value: any, siblings: any) => {
  if (!siblings.starts_at_time || !siblings.ends_at_time || !siblings.date) {
    return true;
  }
  const { start, end } = timeblockToDateRange(siblings);
  if (!start || !end) return true;
  return end > start;
};

// Custom validator: field is required when start OR end time is filled
// Uses siblings to check the current state of all fields
// const requiredWhenHasTime = (value: any, siblings: any) => {
//   // Check if timeblock has any TIME data (start or end time)
//   const hasStart = !!siblings.starts_at_time;
//   const hasEnd = !!siblings.ends_at_time;
//   const hasAnyTimeData = hasStart || hasEnd;

//   // If no time data, no validation needed
//   if (!hasAnyTimeData) return true;

//   // When time data exists, field must have value (not null, undefined, or empty string)
//   return value !== null && value !== undefined && value !== '';
// };

// Vuelidate rules - if start OR end is filled, all three fields are required
const rules = {
  date: {
    requiredWhenHasTime: helpers.withMessage('Vyberte dátum', required),
  },
  starts_at_time: {
    requiredWhenHasTime: helpers.withMessage('Zadajte začiatok', required),
  },
  ends_at_time: {
    requiredWhenHasTime: helpers.withMessage('Zadajte koniec', required),
    timeRangeValid: helpers.withMessage(
      'Koniec musí byť po začiatku',
      timeRangeValid,
    ),
  },
};

const v$ = useVuelidate(rules, props.modelValue, { $autoDirty: true });

// Error visibility logic: show errors if field is dirty OR if the opposite time field is filled
const hasStartTimeError = computed(() => {
  // Show error if field is dirty and has error
  if (v$.value.starts_at_time.$dirty && v$.value.starts_at_time.$error) {
    return true;
  }
  // OR if end time is filled but start is not
  if (props.modelValue.ends_at_time && !props.modelValue.starts_at_time) {
    return v$.value.starts_at_time.$error;
  }
  return false;
});

const hasEndTimeError = computed(() => {
  // Show error if field is dirty and has error
  if (v$.value.ends_at_time.$dirty && v$.value.ends_at_time.$error) {
    return true;
  }
  // OR if start time is filled but end is not
  if (props.modelValue.starts_at_time && !props.modelValue.ends_at_time) {
    return v$.value.ends_at_time.$error;
  }
  return false;
});

const endTimeErrorMessage = computed(() => {
  if (hasEndTimeError.value) {
    return v$.value.ends_at_time.$errors.map((e) => e.$message).join(', ');
  }
  return '';
});

// Reactive refs for composable
const employeeRef = computed(() => props.employee);
const dateRef = computed(() => props.modelValue.date);
const timeblockRef = computed(() => props.modelValue);

// Get other timeblocks for the same date (excluding current one)
// This includes timeblocks from both the current employee and other employees
const otherTimeblocksForSameDate = computed(() => {
  if (!props.modelValue.date) return [];

  const currentDate = moment(props.modelValue.date).format('YYYY-MM-DD');

  // Use allEmployeeTimeblocks which contains timeblocks for this specific employee
  // Filter out the current timeblock by comparing index directly
  return props.allEmployeeTimeblocks.filter((tb, index) => {
    // Skip current timeblock by index
    if (index === props.currentIndex) {
      return false;
    }

    // Must have required fields
    if (!tb.date || !tb.starts_at_time || !tb.ends_at_time) return false;

    // Must be the same date
    return moment(tb.date).format('YYYY-MM-DD') === currentDate;
  });
});

// Get all timeblock IDs being edited locally for this employee (to exclude from server scheduled time)
const allEditingTimeblockIds = computed(() => {
  return props.allEmployeeTimeblocks
    .filter((tb) => tb.id != null)
    .map((tb) => tb.id) as number[];
});

// Use timeline composable
const editingBlockerIdRef = computed(() => props.editingBlockerId);
const draftTimeblocksRef = computed(() => props.draftTimeblocks);

const {
  isLoading,
  timelineTasks,
  scheduledTime: _scheduledTime,
  totalPlannedTime: _totalPlannedTime,
  freeTime: _freeTime,
  timeblockDuration,
  refetch,
} = useEmployeeTimeline(
  employeeRef,
  dateRef,
  timeblockRef,
  otherTimeblocksForSameDate,
  editingBlockerIdRef,
  draftTimeblocksRef,
  allEditingTimeblockIds,
);

// Force refetch when date changes
watch(
  () => props.modelValue.date,
  () => {
    if (props.modelValue.date && props.employee?.id) {
      refetch();
      //   conceptTasksRefetch();
    }
  },
);

onMounted(() => {
  refetch();
  //   conceptTasksRefetch();
});

// All tasks from timelineTasks are already filtered for the selected date
// Filter out timeblocks that are being edited or deleted locally
const scheduledBlocks = computed(() => {
  // Combine IDs that should be filtered: edited locally or deleted
  const hiddenTimeblockIds = new Set([
    // Timeblock IDs being edited locally
    ...props.allTimeblocks.filter((tb) => tb.id).map((tb) => tb.id),
    // Timeblock IDs that have been deleted
    ...(props.deletedTimeblockIds || []),
  ]);

  // Filter out tasks whose timeblock IDs should be hidden
  return timelineTasks.value.filter(
    (task) =>
      !task._timeblock_id || !hiddenTimeblockIds.has(task._timeblock_id),
  );
});

// Convert other unsaved timeblocks to task format for collision detection
const otherTimeblocksAsTasks = computed(() => {
  return otherTimeblocksForSameDate.value
    .map((tb) => {
      const { start, end } = timeblockToDateRange(tb);
      if (!start || !end) return null;

      return {
        _real_start: start,
        _real_end: end,
        _timeblock_id: tb.id || null,
      };
    })
    .filter((task) => task !== null);
});

// Combine scheduled blocks with other unsaved timeblocks for collision detection
const allBlocksForCollisionCheck = computed(() => {
  return [...scheduledBlocks.value, ...otherTimeblocksAsTasks.value];
});

// Recalculate collision using filtered scheduled blocks AND other unsaved timeblocks
// This prevents false collisions with timeblocks being edited, but catches collisions with other unsaved timeblocks
const collision = computed(() => {
  return detectCollision(props.modelValue, allBlocksForCollisionCheck.value);
});

// Recalculate collision segments using filtered scheduled blocks AND other unsaved timeblocks
const collisionSegments = computed(() => {
  return getCollisionSegmentsUtil(
    props.modelValue,
    allBlocksForCollisionCheck.value,
  );
});

// Get current timeblock range
const currentTimeblockRange = computed(() => {
  const { start, end } = timeblockToDateRange(props.modelValue);
  return { start, end };
});

// Validate that end time is after start time
const isTimeRangeValid = computed(() => {
  if (!props.modelValue.starts_at_time || !props.modelValue.ends_at_time) {
    return true; // Don't show error if fields are empty
  }
  const { start, end } = currentTimeblockRange.value;
  if (!start || !end) return true;
  return end > start;
});

// Center timeline around middle of work hours
const centerTimeline = () => {
  if (!timelineRef.value?.timelineScroll) return;

  // Calculate the middle of work hours
  const workStart = props.employee?.work_start ?? 8;
  const workEnd = props.employee?.work_end ?? 17;
  const centerHour = (workStart + workEnd) / 2;

  const centerPosition = (centerHour - START_HOUR) * hourWidth.value;
  const containerWidth = timelineRef.value.timelineScroll.clientWidth;

  timelineRef.value.timelineScroll.scrollLeft =
    centerPosition - containerWidth / 2;
};

// Watch for date changes or employee changes to re-center
watch(
  () => [props.modelValue.date, props.employee?.id],
  () => {
    nextTick(() => {
      centerTimeline();
    });
  },
);

// Watch for timeline ref to become available and center it
watch(timelineRef, (newRef) => {
  if (newRef?.timelineScroll) {
    nextTick(() => {
      centerTimeline();
    });
  }
});

// Watch for loading state - when data finishes loading, center the timeline
watch(isLoading, (newIsLoading, oldIsLoading) => {
  // When loading changes from true to false, timeline is re-rendered
  if (oldIsLoading && !newIsLoading) {
    // Wait for DOM to update, then center
    nextTick(() => {
      setTimeout(() => {
        centerTimeline();
      }, 0); // Small delay to ensure DOM is fully rendered
    });
  }
});

// Date navigation
const previousDay = () => {
  if (!props.modelValue.date) return;

  const currentDate = moment(props.modelValue.date);
  const newDate = currentDate.clone().subtract(1, 'day').toDate();

  // Don't allow going to past dates
  if (moment(newDate).isBefore(moment(), 'day')) return;

  updateDate(newDate);
};

const nextDay = () => {
  if (!props.modelValue.date) return;

  const currentDate = moment(props.modelValue.date);
  const newDate = currentDate.clone().add(1, 'day').toDate();

  updateDate(newDate);
};

// Update handlers
const updateDate = (date: Date | null) => {
  emit('update:modelValue', { ...props.modelValue, date });
};

const updateStartTime = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', {
    ...props.modelValue,
    starts_at_time: target.value,
  });
};

const updateEndTime = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', {
    ...props.modelValue,
    ends_at_time: target.value,
  });
};

// Handle timeblock updated from timeline (drag/drop or quick add)
const handleTimeblockUpdated = (payload: {
  starts_at_time: string;
  ends_at_time: string;
}) => {
  const { starts_at_time } = payload;
  let { ends_at_time } = payload;

  // Fix accidental same start/end time by adding 1 hour to end
  if (starts_at_time === ends_at_time) {
    const [hours, minutes] = starts_at_time.split(':').map(Number);
    const newEndHour = (hours + 1) % 24; // Wrap around at midnight
    ends_at_time = `${String(newEndHour).padStart(2, '0')}:${String(
      minutes,
    ).padStart(2, '0')}`;
  }

  emit('update:modelValue', {
    ...props.modelValue,
    starts_at_time,
    ends_at_time,
  });

  // Trigger validation after timeline update
  nextTick(() => {
    v$.value.$touch();
  });
};

// Expose validate method
const validate = async () => {
  v$.value.$touch();
  const isValid = await v$.value.$validate();
  return isValid && isTimeRangeValid.value;
};

defineExpose({
  validate,
});

watch(
  () => props.modelValue.ends_at_time,
  () => {
    if (v$.value.ends_at_time.$model !== props.modelValue.ends_at_time) {
      v$.value.ends_at_time.$model = props.modelValue.ends_at_time;
    }
  },
);

watch(
  () => props.modelValue.starts_at_time,
  () => {
    if (v$.value.starts_at_time.$model !== props.modelValue.starts_at_time) {
      v$.value.starts_at_time.$model = props.modelValue.starts_at_time;
      console.log('starts_at_time changed', v$.value.starts_at_time.$model);
    }
  },
);

watch(
  () => props.modelValue.date,
  () => {
    if (v$.value.date.$model !== props.modelValue.date) {
      v$.value.date.$model = props.modelValue.date;
    }
  },
);
</script>

<style lang="sass" scoped>
.timeblock
  width: 100%
  border: 1px solid #e5e5e5
  border-radius: 8px
  margin-bottom: 8px
  overflow: hidden

.timeblock--finished
  opacity: 0.4
  pointer-events: none
  cursor: not-allowed
</style>
